import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../../redux/store";
import { deletePage, fetchPages, upsertPage } from "../../../../redux/blogSlice";
import { motion, AnimatePresence } from "framer-motion";
import {
  Pencil, Zap, Copy, Trash2, ExternalLink, Link,
  CheckCircle, AlertCircle, X, RefreshCw, Search, FileText,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────
   FONTS
───────────────────────────────────────────────────────── */
const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700&family=Montserrat:wght@300;400;500;600;700&display=swap');
`;

/* ─────────────────────────────────────────────────────────
   THEME
───────────────────────────────────────────────────────── */
const C = {
  paper:        "#ffffff",
  surface:      "#f7f7f7",
  ink:          "#0a0a0a",
  inkSoft:      "#2a2a2a",
  inkMid:       "#6b6b6b",
  inkLight:     "#a3a3a3",
  border:       "#e0e0e0",
  success:      "#1a7a3a",
  successBg:    "#f0faf4",
  successBorder:"#b8dfc8",
  danger:       "#b22222",
  dangerBg:     "#fff5f5",
  dangerBorder: "#f0c8c8",
  serif:        "'Cormorant Garamond', Georgia, serif",
  sans:         "'Montserrat', sans-serif",
};

const AllPages: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const pages    = useSelector((state: RootState) => state.blog.pages);

  const [search,      setSearch]      = useState("");
  const [deleteId,    setDeleteId]    = useState<string | null>(null);
  const [copiedId,    setCopiedId]    = useState<string | null>(null);
  const [dupId,       setDupId]       = useState<string | null>(null);
  const [toast,       setToast]       = useState<{ type:"success"|"danger"; text:string } | null>(null);
  const [searchFocus, setSearchFocus] = useState(false);

  useEffect(() => { dispatch(fetchPages()); }, [dispatch]);

  const flash = (type:"success"|"danger", text:string) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3000);
  };

  /* ── Original handlers (all preserved) ── */
  const handleEdit = (id: string) => {
    navigate(`/editpages/${id}`);
  };

  const handleDuplicate = async (page: any) => {
    setDupId(page.id);
    const newPage = { ...page, title:`${page.title} Copy`, slug:`${page.slug}-copy` };
    delete newPage.id;
    await dispatch(upsertPage(newPage));
    await dispatch(fetchPages());
    setDupId(null);
    flash("success", `"${page.title}" duplicated.`);
  };

  const handleTrash = async (id: string) => {
    await dispatch(deletePage(id));
    setDeleteId(null);
    flash("danger", "Page moved to trash.");
  };

  const handleCopy = (slug: string, id: string) => {
    navigator.clipboard.writeText(`${window.location.origin}/pages/${slug}`);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2200);
  };

  /* ── Filtered ── */
  const filtered = search.trim()
    ? pages.filter(p => p.title?.toLowerCase().includes(search.toLowerCase()) ||
                        p.slug?.toLowerCase().includes(search.toLowerCase()))
    : pages;

  return (
    <div style={{ background:C.paper, fontFamily:C.sans, color:C.ink }}>
      <style>{FONT_CSS + AP_CSS}</style>

      {/* ── TITLE STRIP ── */}
      <div style={S.titleStrip}>
        <div style={{ height:2, background:C.ink, marginBottom:16 }}/>
        <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", flexWrap:"wrap", gap:8, marginBottom:16 }}>
          <div>
            <p style={S.eye}>CONTENT MANAGEMENT</p>
            <h1 style={S.title}>All Pages</h1>
          </div>
          <span style={S.countPill}>{pages.length} page{pages.length!==1?"s":""}</span>
        </div>
        <div style={{ height:1, background:C.border }}/>
      </div>

      <div style={S.body}>

        {/* ── TOAST ── */}
        <AnimatePresence>
          {toast && (
            <motion.div key="toast"
              initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }}
              exit={{ opacity:0, y:-8 }} transition={{ duration:0.22 }}
              style={{
                display:"flex", alignItems:"center", gap:10,
                padding:"11px 16px", marginBottom:20,
                background:toast.type==="success"?C.successBg:C.dangerBg,
                border:`1px solid ${toast.type==="success"?C.successBorder:C.dangerBorder}`,
              }}>
              {toast.type==="success"
                ? <CheckCircle size={13} color={C.success}/>
                : <AlertCircle size={13} color={C.danger}/>}
              <span style={{ fontSize:11, fontWeight:500, flex:1,
                color:toast.type==="success"?C.success:C.danger }}>
                {toast.text}
              </span>
              <button onClick={()=>setToast(null)} style={{ background:"none", border:"none", cursor:"pointer", color:C.inkMid }}>
                <X size={11}/>
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── TOOLBAR ── */}
        <div style={S.toolbar}>
          <div style={{ ...S.searchWrap, border:`1px solid ${searchFocus?C.ink:C.border}` }}>
            <Search size={13} color={searchFocus?C.ink:C.inkLight} strokeWidth={1.5}/>
            <input type="text" placeholder="Search pages…" value={search}
              onChange={e => setSearch(e.target.value)}
              onFocus={()=>setSearchFocus(true)} onBlur={()=>setSearchFocus(false)}
              style={S.searchInput} className="ap-input"/>
            {search && (
              <button onClick={()=>setSearch("")}
                style={{ background:"none", border:"none", cursor:"pointer", color:C.inkMid, display:"flex" }}>
                <X size={11}/>
              </button>
            )}
          </div>
          <button onClick={()=>dispatch(fetchPages())} style={S.refreshBtn} title="Refresh">
            <RefreshCw size={13} strokeWidth={1.5}/>
          </button>
        </div>

        {/* ── EMPTY STATE ── */}
        {filtered.length === 0 && (
          <div style={S.empty}>
            <FileText size={32} color={C.border} strokeWidth={1}/>
            <p style={{ fontFamily:C.serif, fontSize:22, fontWeight:300, color:C.ink, marginTop:12 }}>
              {search ? "No pages found" : "No pages yet"}
            </p>
            <p style={{ fontSize:11, color:C.inkLight, marginTop:6 }}>
              {search ? `No results for "${search}"` : "Create your first page using the Pages tab"}
            </p>
          </div>
        )}

        {/* ── TABLE ── */}
        {filtered.length > 0 && (
          <div>
            {/* Table header */}
            <div style={S.tableHead}>
              <div style={{ flex:1 }}>TITLE</div>
              <div style={S.slugCol} className="ap-slug-col">SLUG</div>
              <div style={S.actionsCol}>ACTIONS</div>
            </div>

            {filtered.map((page, idx) => (
              <motion.div
                key={page.id}
                initial={{ opacity:0, y:8 }}
                animate={{ opacity:1, y:0 }}
                transition={{ delay:idx*0.04, duration:0.28 }}
                style={{
                  ...S.row,
                  borderBottom: idx===filtered.length-1?`1px solid ${C.border}`:"none",
                }}
                className="ap-row"
              >
                {/* Title + slug */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={S.pageTitle}>{page.title || "(no title)"}</div>
                  <div style={S.pageSlug} className="ap-slug-mobile">/{page.slug}</div>
                </div>

                {/* Slug column — desktop only */}
                <div style={S.slugCol} className="ap-slug-col">
                  <span style={S.slugText}>/{page.slug}</span>
                </div>

                {/* Actions */}
                <div style={S.actionsCol}>
                  <div style={S.actions}>

                    <button onClick={()=>handleEdit(page.id)}
                      style={S.actionBtn} className="ap-action" title="Edit">
                      <Pencil size={11}/><span className="ap-action-label">Edit</span>
                    </button>

                    {/* Quick Edit — navigates same as Edit per original */}
                    <button onClick={()=>handleEdit(page.id)}
                      style={S.actionBtn} className="ap-action" title="Quick Edit">
                      <Zap size={11}/><span className="ap-action-label">Quick</span>
                    </button>

                    <button onClick={()=>handleDuplicate(page)}
                      disabled={dupId===page.id}
                      style={{ ...S.actionBtn, opacity:dupId===page.id?0.5:1 }}
                      className="ap-action" title="Duplicate">
                      <Copy size={11}/>
                      <span className="ap-action-label">{dupId===page.id?"…":"Dup"}</span>
                    </button>

                    {/* Two-step delete */}
                    {deleteId===page.id ? (
                      <div style={S.deleteConfirm}>
                        <span style={{ fontSize:9, color:C.danger, fontWeight:600 }}>Delete?</span>
                        <button onClick={()=>handleTrash(page.id)} style={S.deleteYes}>Yes</button>
                        <button onClick={()=>setDeleteId(null)} style={S.deleteNo}>No</button>
                      </div>
                    ) : (
                      <button onClick={()=>setDeleteId(page.id)}
                        style={{ ...S.actionBtn, color:C.danger }}
                        className="ap-action" title="Trash">
                        <Trash2 size={11}/><span className="ap-action-label">Trash</span>
                      </button>
                    )}

                    <button onClick={()=>window.open(`/pages/${page.slug}`, "_blank")}
                      style={S.actionBtn} className="ap-action" title="View">
                      <ExternalLink size={11}/><span className="ap-action-label">View</span>
                    </button>

                    <button
                      onClick={()=>handleCopy(page.slug, page.id)}
                      style={{ ...S.actionBtn, color:copiedId===page.id?C.success:C.inkMid }}
                      className="ap-action" title="Copy link">
                      <Link size={11}/>
                      <span className="ap-action-label">{copiedId===page.id?"✓":"Copy"}</span>
                    </button>

                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AllPages;

/* ─────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────── */
const S: { [key: string]: React.CSSProperties } = {
  titleStrip: { padding:"20px 20px 0" },
  eye:       { fontSize:9, letterSpacing:"0.3em", color:C.inkMid, fontFamily:C.sans, marginBottom:6 },
  title:     { fontFamily:C.serif, fontSize:"clamp(26px,4vw,42px)" as any, fontWeight:600, color:C.ink, lineHeight:1, margin:0, letterSpacing:"-0.01em" },
  countPill: { fontSize:10, letterSpacing:"0.14em", color:C.inkMid, fontFamily:C.sans, paddingBottom:4 },

  body: { maxWidth:1160, margin:"0 auto", padding:"24px 20px 60px" },

  toolbar: { display:"flex", alignItems:"center", gap:10, marginBottom:20 },
  searchWrap: {
    display:"flex", alignItems:"center", gap:8,
    background:C.surface, padding:"8px 14px",
    flex:1, maxWidth:320, transition:"border-color 0.2s",
  },
  searchInput: {
    flex:1, background:"transparent", border:"none", outline:"none",
    fontSize:12, color:C.ink, fontFamily:C.sans,
  },
  refreshBtn: {
    background:"transparent", border:`1px solid ${C.border}`,
    width:36, height:36, display:"flex", alignItems:"center",
    justifyContent:"center", cursor:"pointer", color:C.inkMid, flexShrink:0,
  },

  empty: {
    textAlign:"center" as const, padding:"64px 20px",
    display:"flex", flexDirection:"column", alignItems:"center",
  },

  /* Table */
  tableHead: {
    display:"flex", alignItems:"center", gap:16,
    padding:"8px 0",
    borderTop:`2px solid ${C.ink}`,
    borderBottom:`1px solid ${C.border}`,
    fontSize:8, letterSpacing:"0.22em", color:C.inkMid,
    fontFamily:C.sans, fontWeight:600,
  },
  row: {
    display:"flex", alignItems:"center", gap:16,
    padding:"13px 0",
    borderTop:`1px solid ${C.border}`,
    transition:"background 0.15s",
  },

  pageTitle: {
    fontSize:14, fontWeight:500, color:C.ink,
    fontFamily:C.serif, letterSpacing:"0.01em",
    overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" as const,
  },
  pageSlug: {
    fontSize:9, color:C.inkLight, marginTop:3,
    fontFamily:"'Courier New',monospace",
  },

  slugCol: {
    width:160, flexShrink:0, minWidth:0,
    overflow:"hidden",
  },
  slugText: {
    fontSize:10, color:C.inkLight,
    fontFamily:"'Courier New',monospace",
    overflow:"hidden", textOverflow:"ellipsis",
    whiteSpace:"nowrap" as const, display:"block",
  },

  actionsCol: { flexShrink:0, width:"auto" },
  actions: { display:"flex", flexWrap:"wrap", gap:4 },
  actionBtn: {
    display:"inline-flex", alignItems:"center", gap:4,
    background:"transparent", border:`1px solid ${C.border}`,
    color:C.inkMid, padding:"5px 8px",
    fontSize:9, fontWeight:500, letterSpacing:"0.08em",
    cursor:"pointer", fontFamily:C.sans,
    transition:"border-color 0.15s, color 0.15s",
    whiteSpace:"nowrap" as const,
  },

  deleteConfirm: {
    display:"inline-flex", alignItems:"center", gap:5,
    background:C.dangerBg, border:`1px solid ${C.dangerBorder}`,
    padding:"4px 8px",
  },
  deleteYes: {
    background:C.danger, color:"#fff", border:"none",
    padding:"3px 8px", fontSize:9, fontWeight:700,
    cursor:"pointer", fontFamily:C.sans,
  },
  deleteNo: {
    background:"transparent", border:`1px solid ${C.border}`,
    color:C.inkMid, padding:"3px 8px",
    fontSize:9, cursor:"pointer", fontFamily:C.sans,
  },
};

/* ─────────────────────────────────────────────────────────
   CSS
───────────────────────────────────────────────────────── */
const AP_CSS = `
  * { box-sizing: border-box; }
  ::-webkit-scrollbar { width: 0; }

  .ap-input:focus    { outline: none; }
  input::placeholder { color: #a3a3a3; font-size: 12px; }

  .ap-row:hover { background: #f7f7f7; }
  .ap-action:hover { border-color: #0a0a0a !important; color: #0a0a0a !important; }

  /* Hide slug column on mobile — slug is shown inline under title instead */
  @media (max-width: 640px) {
    .ap-slug-col { display: none !important; }
    .ap-action-label { display: none; }
  }

  /* Show inline slug under title on mobile */
  .ap-slug-mobile { display: none; }
  @media (max-width: 640px) {
    .ap-slug-mobile { display: block !important; }
  }
`;